import { useState, useEffect, useMemo } from 'react';
import { X, RefreshCw, BarChart2, Calendar, PiggyBank, ChevronLeft, Percent, Wallet } from 'lucide-react';
import { useCreateForecast, useUpdateForecast } from '../../hooks/useForecastMutations.js';
import { useCreateIndexedExpense, useUpdateIndexedExpense } from '../../hooks/useIndexedExpenses.js';
import { useCreateMonthlyBudget, useUpdateMonthlyBudget } from '../../hooks/useMonthlyBudgets.js';
import { useRevenueHypothesis } from '../../hooks/useRevenueHypothesis.js';
import { useCategories } from '../../hooks/useCategories.js';
import { useEntityScope } from '../../hooks/useEntityScope.js';
import { formatCurrency } from '../../lib/format.js';
import Button from '../Button.jsx';
import IntercompanyConfigPanel from '../intercompany/IntercompanyConfigPanel.jsx';

// ── Constantes ──────────────────────────────────────────────────────────────

const VAT_RATES = [
  { value: '0', label: '0 %' },
  { value: '5.5', label: '5,5 %' },
  { value: '10', label: '10 %' },
  { value: '20', label: '20 %' },
];

const MODES = [
  {
    value: 'recurring',
    label: 'Récurrent',
    Icon: RefreshCw,
    description: 'Date et montant fixes qui se répètent (loyer, salaires…)',
    color: 'bg-blue-50 border-blue-200 text-blue-700',
    selectedColor: 'bg-blue-100 border-blue-500',
  },
  {
    value: 'budget',
    label: 'Budget annuel',
    Icon: BarChart2,
    description: 'Enveloppe annuelle distribuée pro-rata (carburant, fournitures…)',
    color: 'bg-purple-50 border-purple-200 text-purple-700',
    selectedColor: 'bg-purple-100 border-purple-500',
    hidden: true,
  },
  {
    value: 'one_shot',
    label: 'Ponctuel',
    Icon: Calendar,
    description: 'Une date, un montant (licence, taxe foncière…)',
    color: 'bg-amber-50 border-amber-200 text-amber-700',
    selectedColor: 'bg-amber-100 border-amber-500',
  },
  {
    value: 'provision',
    label: 'Provision',
    Icon: PiggyBank,
    description: 'Poche alimentée chaque mois pour lisser des dépenses irrégulières',
    color: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    selectedColor: 'bg-emerald-100 border-emerald-500',
    hidden: true,
  },
  {
    value: 'indexed',
    label: 'Indexée sur le CA',
    Icon: Percent,
    description: 'Recalculée auto à partir des hypothèses revenus (ex. 8 % du CA pour les expéditions)',
    color: 'bg-rose-50 border-rose-200 text-rose-700',
    selectedColor: 'bg-rose-100 border-rose-500',
  },
  {
    value: 'monthly_budget',
    label: 'Budget mensuel',
    Icon: Wallet,
    description: 'Cible mensuelle de dépenses sur une catégorie (FB Ads, carburant…)',
    color: 'bg-violet-50 border-violet-200 text-violet-700',
    selectedColor: 'bg-violet-100 border-violet-500',
  },
];

const FREQUENCIES = [
  { value: 'monthly', label: 'Mensuel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'yearly', label: 'Annuel' },
  { value: 'custom', label: 'Personnalisé' },
];

const YEARS = [2026, 2027, 2028];

const inputClass =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 disabled:opacity-50';

// ── Helpers ──────────────────────────────────────────────────────────────────

function Field({ label, children, error, required, hint }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function centsToEuros(cents) {
  if (cents == null) return '';
  return String(Math.abs(cents) / 100);
}

function eurosToCents(str) {
  const v = parseFloat(str);
  return isNaN(v) ? null : Math.round(v * 100);
}

/**
 * Calcule HT et TVA depuis un montant TTC et un taux (en %).
 * Retourne null si taux = 0 ou montant non valide.
 */
function computeVatBreakdown(ttcStr, vatRateStr) {
  const ttc = parseFloat(ttcStr);
  const rate = parseFloat(vatRateStr);
  if (!ttcStr || isNaN(ttc) || ttc <= 0 || isNaN(rate) || rate <= 0) return null;
  const ttcCents = Math.round(ttc * 100);
  const htCents = Math.round(ttcCents / (1 + rate / 100));
  const vatCents = ttcCents - htCents;
  return { htCents, vatCents };
}

// Applique le signe selon le flow_type (expense = négatif, income = positif).
// Pour provision, toujours positif (montant d'alimentation).
function applySign(cents, flowType, mode) {
  if (mode === 'provision') return Math.abs(cents);
  if (flowType === 'expense') return -Math.abs(cents);
  return Math.abs(cents);
}

// ── Sélecteur TVA partagé ─────────────────────────────────────────────────────

/**
 * Radio group horizontal pour le taux de TVA + input custom.
 * Utilisé dans RecurringForm, BudgetForm, OneShotForm.
 */
function VatRateSelector({ vatRate, customVatRate, onVatRateChange, onCustomVatRateChange, disabled }) {
  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-slate-700">Taux TVA</label>
      <div className="flex flex-wrap gap-2">
        {VAT_RATES.map((r) => (
          <button
            key={r.value}
            type="button"
            onClick={() => onVatRateChange(r.value)}
            disabled={disabled}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${
              vatRate === r.value
                ? 'bg-slate-800 border-slate-800 text-white'
                : 'bg-white border-slate-300 text-slate-600 hover:border-slate-500'
            }`}
            aria-pressed={vatRate === r.value}
          >
            {r.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onVatRateChange('custom')}
          disabled={disabled}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${
            vatRate === 'custom'
              ? 'bg-slate-800 border-slate-800 text-white'
              : 'bg-white border-slate-300 text-slate-600 hover:border-slate-500'
          }`}
          aria-pressed={vatRate === 'custom'}
        >
          Autre
        </button>
      </div>
      {vatRate === 'custom' && (
        <input
          type="number"
          className={`${inputClass} w-36`}
          value={customVatRate}
          onChange={(e) => onCustomVatRateChange(e.target.value)}
          min="0"
          max="100"
          step="0.01"
          placeholder="Ex : 8.5"
          disabled={disabled}
          aria-label="Taux TVA personnalisé (%)"
        />
      )}
    </div>
  );
}

/**
 * Helper live affiché sous le montant TTC.
 * Affiche "soit X HT + Y TVA (rate %)" uniquement si rate > 0 et montant > 0.
 */
function VatHelper({ amountStr, vatRate, customVatRate }) {
  const effectiveRate = vatRate === 'custom' ? customVatRate : vatRate;
  const breakdown = computeVatBreakdown(amountStr, effectiveRate);
  if (!breakdown) return null;

  const rateLabel = effectiveRate ? `${parseFloat(effectiveRate).toLocaleString('fr-FR')} %` : '';
  const htEuros = (breakdown.htCents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const vatEuros = (breakdown.vatCents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <p className="text-[11px] text-slate-400 mt-1">
      soit <strong className="text-slate-500">{htEuros} € HT</strong>
      {' + '}
      <strong className="text-slate-500">{vatEuros} € TVA</strong>
      {' '}({rateLabel})
    </p>
  );
}

// ── Formulaires par mode ──────────────────────────────────────────────────────

function RecurringForm({ form, setField, errors, disabled }) {
  return (
    <div className="space-y-4">
      <Field label="Libellé" error={errors.label_pattern} required>
        <input
          type="text"
          className={inputClass}
          value={form.label_pattern}
          onChange={(e) => setField('label_pattern', e.target.value)}
          placeholder="Ex : Loyer bureau"
          disabled={disabled}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Field label="Montant TTC (€)" error={errors.amount} required>
            <input
              type="number"
              className={inputClass}
              value={form.amount}
              onChange={(e) => setField('amount', e.target.value)}
              min="0.01"
              step="0.01"
              placeholder="1500"
              disabled={disabled}
            />
          </Field>
          <VatHelper amountStr={form.amount} vatRate={form.vatRate} customVatRate={form.customVatRate} />
        </div>

        <Field label="Fréquence" error={errors.frequency} required>
          <select
            className={inputClass}
            value={form.frequency}
            onChange={(e) => setField('frequency', e.target.value)}
            disabled={disabled}
          >
            {FREQUENCIES.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </Field>
      </div>

      <VatRateSelector
        vatRate={form.vatRate}
        customVatRate={form.customVatRate}
        onVatRateChange={(v) => setField('vatRate', v)}
        onCustomVatRateChange={(v) => setField('customVatRate', v)}
        disabled={disabled}
      />

      <div className="grid grid-cols-2 gap-3">
        <Field label="Date de début" error={errors.start_date} required>
          <input
            type="date"
            className={inputClass}
            value={form.start_date}
            onChange={(e) => setField('start_date', e.target.value)}
            disabled={disabled}
          />
        </Field>

        <Field label="Date de fin" error={errors.end_date} hint="Optionnelle">
          <input
            type="date"
            className={inputClass}
            value={form.end_date}
            onChange={(e) => setField('end_date', e.target.value)}
            disabled={disabled}
          />
        </Field>
      </div>
    </div>
  );
}

function BudgetForm({ form, setField, errors, disabled }) {
  const monthly =
    form.amount && !isNaN(parseFloat(form.amount))
      ? (parseFloat(form.amount) / 12).toFixed(2)
      : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Année" error={errors.year} required>
          <select
            className={inputClass}
            value={form.year}
            onChange={(e) => setField('year', e.target.value)}
            disabled={disabled}
          >
            {YEARS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </Field>

        <div className="space-y-1">
          <Field
            label="Montant annuel TTC (€)"
            error={errors.amount}
            required
            hint={monthly ? `soit ${monthly} €/mois` : undefined}
          >
            <input
              type="number"
              className={inputClass}
              value={form.amount}
              onChange={(e) => setField('amount', e.target.value)}
              min="0.01"
              step="0.01"
              placeholder="5000"
              disabled={disabled}
            />
          </Field>
          <VatHelper amountStr={form.amount} vatRate={form.vatRate} customVatRate={form.customVatRate} />
        </div>
      </div>

      <VatRateSelector
        vatRate={form.vatRate}
        customVatRate={form.customVatRate}
        onVatRateChange={(v) => setField('vatRate', v)}
        onCustomVatRateChange={(v) => setField('customVatRate', v)}
        disabled={disabled}
      />

      {monthly && (
        <div className="rounded-lg bg-purple-50 border border-purple-200 px-3 py-2 text-xs text-purple-700">
          Soit {monthly} €/mois lissés sur 12 mois
        </div>
      )}
    </div>
  );
}

function OneShotForm({ form, setField, errors, disabled }) {
  return (
    <div className="space-y-4">
      <Field label="Libellé" error={errors.label} required>
        <input
          type="text"
          className={inputClass}
          value={form.label}
          onChange={(e) => setField('label', e.target.value)}
          placeholder="Ex : Renouvellement licence"
          disabled={disabled}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Field label="Montant TTC (€)" error={errors.amount} required>
            <input
              type="number"
              className={inputClass}
              value={form.amount}
              onChange={(e) => setField('amount', e.target.value)}
              min="0.01"
              step="0.01"
              placeholder="2800"
              disabled={disabled}
            />
          </Field>
          <VatHelper amountStr={form.amount} vatRate={form.vatRate} customVatRate={form.customVatRate} />
        </div>

        <Field label="Date" error={errors.due_date} required>
          <input
            type="date"
            className={inputClass}
            value={form.due_date}
            onChange={(e) => setField('due_date', e.target.value)}
            disabled={disabled}
          />
        </Field>
      </div>

      <VatRateSelector
        vatRate={form.vatRate}
        customVatRate={form.customVatRate}
        onVatRateChange={(v) => setField('vatRate', v)}
        onCustomVatRateChange={(v) => setField('customVatRate', v)}
        disabled={disabled}
      />
    </div>
  );
}

function ProvisionForm({ form, setField, errors, disabled }) {
  return (
    <div className="space-y-4">
      <Field label="Nom de la provision" error={errors.name} required>
        <input
          type="text"
          className={inputClass}
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          placeholder="Ex : Provision entretien véhicules"
          disabled={disabled}
        />
      </Field>

      <Field label="Alimentation mensuelle (€)" error={errors.funding} required>
        <input
          type="number"
          className={inputClass}
          value={form.funding}
          onChange={(e) => setField('funding', e.target.value)}
          min="0.01"
          step="0.01"
          placeholder="500"
          disabled={disabled}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Plafond soft (€)" error={errors.cap_soft} hint="Optionnel — stoppe l'alim">
          <input
            type="number"
            className={inputClass}
            value={form.cap_soft}
            onChange={(e) => setField('cap_soft', e.target.value)}
            min="0.01"
            step="0.01"
            placeholder="6000"
            disabled={disabled}
          />
        </Field>

        <Field label="Plafond hard (€)" error={errors.cap_hard} hint="Optionnel — jamais au-delà">
          <input
            type="number"
            className={inputClass}
            value={form.cap_hard}
            onChange={(e) => setField('cap_hard', e.target.value)}
            min="0.01"
            step="0.01"
            placeholder="8000"
            disabled={disabled}
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Date de début" error={errors.start_date} required>
          <input
            type="date"
            className={inputClass}
            value={form.start_date}
            onChange={(e) => setField('start_date', e.target.value)}
            disabled={disabled}
          />
        </Field>

        <Field label="Date de fin" error={errors.end_date} hint="Optionnelle">
          <input
            type="date"
            className={inputClass}
            value={form.end_date}
            onChange={(e) => setField('end_date', e.target.value)}
            disabled={disabled}
          />
        </Field>
      </div>

      <Field label="Notes">
        <textarea
          className={`${inputClass} resize-none`}
          value={form.notes}
          onChange={(e) => setField('notes', e.target.value)}
          rows={2}
          placeholder="Informations complémentaires…"
          disabled={disabled}
        />
      </Field>
    </div>
  );
}

function IndexedForm({ form, setField, errors, disabled, hypothesis }) {
  const pct = parseFloat(form.pct) || 0;
  const baseline = hypothesis?.baseline_cents_monthly;
  const estimated = baseline != null && pct > 0
    ? Math.round(baseline * pct / 100)
    : null;

  return (
    <div className="space-y-4">
      <Field label="Nom de la dépense" error={errors.name} required>
        <input
          type="text"
          className={inputClass}
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          placeholder="Ex : Frais d'expédition"
          disabled={disabled}
        />
      </Field>

      <div className="space-y-1">
        <Field label="Pourcentage du CA (%)" error={errors.pct} required>
          <input
            type="number"
            className={inputClass}
            value={form.pct}
            onChange={(e) => setField('pct', e.target.value)}
            min="0.01"
            step="0.01"
            placeholder="8.5"
            disabled={disabled}
          />
        </Field>
        {estimated != null ? (
          <p className="text-[11px] text-slate-400">
            A CA estimé {formatCurrency(baseline)}/mois (médian),
            cela représente{' '}
            <strong className="text-slate-600">~{formatCurrency(estimated)}/mois</strong>
          </p>
        ) : baseline == null ? (
          <p className="text-[11px] text-amber-600">
            Aucune hypothese de revenus configurée — le montant sera calculé à la volée.
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Date de début" error={errors.start_date} required>
          <input
            type="date"
            className={inputClass}
            value={form.start_date}
            onChange={(e) => setField('start_date', e.target.value)}
            disabled={disabled}
          />
        </Field>

        <Field label="Date de fin" error={errors.end_date} hint="Optionnelle">
          <input
            type="date"
            className={inputClass}
            value={form.end_date}
            onChange={(e) => setField('end_date', e.target.value)}
            disabled={disabled}
          />
        </Field>
      </div>

      <VatRateSelector
        vatRate={form.vatRate}
        customVatRate={form.customVatRate}
        onVatRateChange={(v) => setField('vatRate', v)}
        onCustomVatRateChange={(v) => setField('customVatRate', v)}
        disabled={disabled}
      />
    </div>
  );
}

function MonthlyBudgetForm({ form, setField, errors, disabled }) {
  const today = new Date();
  const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;

  return (
    <div className="space-y-4">
      <Field label="Nom du budget" error={errors.name} required>
        <input
          type="text"
          className={inputClass}
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          placeholder="Ex : FB Ads, Carburant…"
          disabled={disabled}
        />
      </Field>

      <Field label="Montant mensuel (€ TTC)" error={errors.budget_cents_monthly} required>
        <input
          type="number"
          className={inputClass}
          value={form.budget_amount}
          onChange={(e) => setField('budget_amount', e.target.value)}
          min="0.01"
          step="0.01"
          placeholder="500"
          disabled={disabled}
        />
        <VatHelper amountStr={form.budget_amount} vatRate={form.vatRate} customVatRate={form.customVatRate} />
      </Field>

      <VatRateSelector
        vatRate={form.vatRate}
        customVatRate={form.customVatRate}
        onVatRateChange={(v) => setField('vatRate', v)}
        onCustomVatRateChange={(v) => setField('customVatRate', v)}
        disabled={disabled}
      />

      <div className="grid grid-cols-2 gap-3">
        <Field label="Date de début" error={errors.start_date} required>
          <input
            type="date"
            className={inputClass}
            value={form.start_date || firstOfMonth}
            onChange={(e) => setField('start_date', e.target.value)}
            disabled={disabled}
          />
        </Field>

        <Field label="Date de fin" error={errors.end_date} hint="Optionnelle">
          <input
            type="date"
            className={inputClass}
            value={form.end_date}
            onChange={(e) => setField('end_date', e.target.value)}
            disabled={disabled}
          />
        </Field>
      </div>

      <Field label="Notes">
        <textarea
          className={`${inputClass} resize-none`}
          value={form.notes}
          onChange={(e) => setField('notes', e.target.value)}
          rows={2}
          placeholder="Informations complémentaires…"
          disabled={disabled}
        />
      </Field>
    </div>
  );
}

// ── Formulaires initiaux par mode ─────────────────────────────────────────────

// Champs TVA partagés pour les modes qui l'utilisent (sauf provision)
const VAT_DEFAULTS = { vatRate: '20', customVatRate: '' };

function emptyForm(mode) {
  switch (mode) {
    case 'recurring':
      return { label_pattern: '', amount: '', frequency: 'monthly', start_date: '', end_date: '', ...VAT_DEFAULTS };
    case 'budget':
      return { year: String(new Date().getFullYear()), amount: '', ...VAT_DEFAULTS };
    case 'one_shot':
      return { label: '', amount: '', due_date: '', ...VAT_DEFAULTS };
    case 'provision':
      return { name: '', funding: '', cap_soft: '', cap_hard: '', start_date: '', end_date: '', notes: '' };
    case 'indexed': {
      const today = new Date().toISOString().slice(0, 10);
      return { name: '', pct: '', start_date: today, end_date: '', ...VAT_DEFAULTS };
    }
    case 'monthly_budget': {
      const now = new Date();
      const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
      return { name: '', budget_amount: '', start_date: firstOfMonth, end_date: '', notes: '', ...VAT_DEFAULTS };
    }
    default:
      return {};
  }
}

/**
 * Déduit vatRate / customVatRate depuis item.vat_rate (number | null).
 * Si vat_rate correspond à un taux standard → sélectionne ce taux.
 * Sinon → 'custom' + valeur brute dans customVatRate.
 */
function vatRateFromItem(item) {
  if (item.vat_rate == null) return { vatRate: '0', customVatRate: '' };
  const standard = VAT_RATES.find((r) => parseFloat(r.value) === Number(item.vat_rate));
  if (standard) return { vatRate: standard.value, customVatRate: '' };
  return { vatRate: 'custom', customVatRate: String(item.vat_rate) };
}

function formFromItem(item) {
  if (!item) return {};
  switch (item.mode) {
    case 'recurring':
      return {
        label_pattern: item.label_pattern ?? item.label ?? '',
        // Utilise amount_cents_ttc si disponible (backend Sprint 1), sinon amount_cents
        amount: centsToEuros(item.amount_cents_ttc ?? item.amount_cents),
        frequency: item.frequency ?? 'monthly',
        start_date: item.start_date ?? '',
        end_date: item.end_date ?? '',
        ...vatRateFromItem(item),
      };
    case 'budget':
      return {
        year: item.year ? String(item.year) : String(new Date().getFullYear()),
        amount: centsToEuros(item.amount_cents_ttc ?? item.amount_cents),
        ...vatRateFromItem(item),
      };
    case 'one_shot':
      return {
        label: item.label ?? '',
        amount: centsToEuros(item.amount_cents_ttc ?? item.amount_cents),
        due_date: item.due_date ?? item.scheduled_date ?? '',
        ...vatRateFromItem(item),
      };
    case 'provision':
      return {
        name: item.name ?? '',
        funding: centsToEuros(item.funding_cents_monthly),
        cap_soft: centsToEuros(item.cap_soft_cents),
        cap_hard: centsToEuros(item.cap_hard_cents),
        start_date: item.start_date ?? '',
        end_date: item.end_date ?? '',
        notes: item.notes ?? '',
      };
    case 'indexed':
      return {
        name: item.name ?? '',
        pct: item.pct_of_revenue != null ? String(item.pct_of_revenue) : '',
        start_date: item.start_date ?? '',
        end_date: item.end_date ?? '',
        ...vatRateFromItem(item),
      };
    case 'monthly_budget':
      return {
        name: item.name ?? '',
        budget_amount: centsToEuros(item.budget_cents_monthly),
        start_date: item.start_date ?? '',
        end_date: item.end_date ?? '',
        notes: item.notes ?? '',
        ...vatRateFromItem(item),
      };
    default:
      return {};
  }
}

// ── Validation ────────────────────────────────────────────────────────────────

function validate(mode, form) {
  const errors = {};
  const requireAmount = (key = 'amount') => {
    const v = parseFloat(form[key]);
    if (!form[key] || isNaN(v) || v <= 0) errors[key] = 'Montant requis (> 0)';
  };

  switch (mode) {
    case 'recurring':
      if (!form.label_pattern?.trim()) errors.label_pattern = 'Libellé requis';
      requireAmount();
      if (!form.frequency) errors.frequency = 'Fréquence requise';
      if (!form.start_date) errors.start_date = 'Date de début requise';
      if (form.end_date && form.start_date && form.end_date <= form.start_date)
        errors.end_date = 'La fin doit être après le début';
      break;
    case 'budget':
      if (!form.year) errors.year = 'Année requise';
      requireAmount();
      break;
    case 'one_shot':
      if (!form.label?.trim()) errors.label = 'Libellé requis';
      requireAmount();
      if (!form.due_date) errors.due_date = 'Date requise';
      break;
    case 'provision':
      if (!form.name?.trim()) errors.name = 'Nom requis';
      requireAmount('funding');
      if (!form.start_date) errors.start_date = 'Date de début requise';
      if (form.end_date && form.start_date && form.end_date <= form.start_date)
        errors.end_date = 'La fin doit être après le début';
      if (form.cap_soft) {
        const v = parseFloat(form.cap_soft);
        if (isNaN(v) || v <= 0) errors.cap_soft = 'Plafond soft invalide';
      }
      if (form.cap_hard) {
        const v = parseFloat(form.cap_hard);
        if (isNaN(v) || v <= 0) errors.cap_hard = 'Plafond hard invalide';
        if (form.cap_soft && parseFloat(form.cap_hard) <= parseFloat(form.cap_soft))
          errors.cap_hard = 'Le plafond hard doit dépasser le soft';
      }
      break;
    case 'indexed':
      if (!form.name?.trim()) errors.name = 'Nom requis';
      {
        const v = parseFloat(form.pct);
        if (!form.pct || isNaN(v) || v <= 0) errors.pct = 'Pourcentage requis (> 0)';
      }
      if (!form.start_date) errors.start_date = 'Date de début requise';
      if (form.end_date && form.start_date && form.end_date <= form.start_date)
        errors.end_date = 'La fin doit être après le début';
      break;
    case 'monthly_budget':
      if (!form.name?.trim()) errors.name = 'Nom requis';
      {
        const v = parseFloat(form.budget_amount);
        if (!form.budget_amount || isNaN(v) || v <= 0)
          errors.budget_cents_monthly = 'Montant requis (> 0)';
      }
      if (!form.start_date) errors.start_date = 'Date de début requise';
      if (form.end_date && form.start_date && form.end_date <= form.start_date)
        errors.end_date = 'La fin doit être après le début';
      break;
    default:
      break;
  }
  return errors;
}

// ── Payload builders ──────────────────────────────────────────────────────────

/**
 * Résout le vat_rate effectif depuis form.vatRate / form.customVatRate.
 * Retourne null si taux = '0' ou non applicable.
 */
function resolveVatRate(form) {
  if (!form.vatRate || form.vatRate === '0') return null;
  if (form.vatRate === 'custom') {
    const v = parseFloat(form.customVatRate);
    return isNaN(v) || v <= 0 ? null : v;
  }
  return parseFloat(form.vatRate);
}

function buildPayload(mode, form, categoryId, flowType) {
  const sign = (euros) => applySign(eurosToCents(euros), flowType, mode);
  const vatRate = mode !== 'provision' ? resolveVatRate(form) : undefined;

  switch (mode) {
    case 'recurring':
      return {
        mode,
        category_id: categoryId,
        label_pattern: form.label_pattern.trim(),
        amount_cents: sign(form.amount),
        frequency: form.frequency,
        start_date: form.start_date,
        end_date: form.end_date || null,
        vat_rate: vatRate ?? null,
      };
    case 'budget':
      return {
        mode,
        category_id: categoryId,
        year: parseInt(form.year, 10),
        amount_cents: sign(form.amount),
        vat_rate: vatRate ?? null,
      };
    case 'one_shot':
      // Le frontend envoie le montant TTC ; le backend convertit en HT pour future_expenses
      return {
        mode,
        category_id: categoryId,
        label: form.label.trim(),
        amount_cents: sign(form.amount),
        due_date: form.due_date,
        vat_rate: vatRate ?? null,
      };
    case 'provision':
      return {
        mode,
        category_id: categoryId,
        name: form.name.trim(),
        funding_cents_monthly: eurosToCents(form.funding),
        cap_soft_cents: form.cap_soft ? eurosToCents(form.cap_soft) : null,
        cap_hard_cents: form.cap_hard ? eurosToCents(form.cap_hard) : null,
        start_date: form.start_date,
        end_date: form.end_date || null,
        notes: form.notes?.trim() || null,
      };
    case 'indexed':
      // Option A : bypass /forecasts → appel direct /indexed-expenses
      return {
        _isIndexed: true,
        name: form.name.trim(),
        category_id: categoryId,
        pct_of_revenue: parseFloat(form.pct),
        start_date: form.start_date,
        end_date: form.end_date || null,
        vat_rate: vatRate ?? null,
      };
    case 'monthly_budget':
      // Bypass /forecasts → appel direct /monthly-budgets
      return {
        _isMonthlyBudget: true,
        name: form.name.trim(),
        category_id: categoryId,
        budget_cents_monthly: eurosToCents(form.budget_amount),
        start_date: form.start_date,
        end_date: form.end_date || null,
        vat_rate: vatRate ?? null,
        notes: form.notes?.trim() || null,
      };
    default:
      return {};
  }
}

// ── Composant principal ───────────────────────────────────────────────────────

/**
 * Modal d'ajout / édition d'un prévisionnel.
 *
 * Props :
 *   isOpen        — booléen
 *   onClose       — callback fermeture
 *   categoryId    — UUID de la catégorie cible
 *   categoryName  — nom affiché dans le titre
 *   categoryFlowType — 'expense' | 'income' | 'neutral'
 *   editItem      — si défini, mode édition : item pré-rempli, mode verrouillé
 */
export default function ForecastAddModal({
  isOpen,
  onClose,
  categoryId,
  categoryName,
  categoryFlowType = 'expense',
  editItem = null,
}) {
  const isEdit = Boolean(editItem);
  const needsCategoryStep = !categoryId && !isEdit;

  const [step, setStep] = useState(isEdit ? 'form' : needsCategoryStep ? 'category' : 'mode');
  const [selectedMode, setSelectedMode] = useState(editItem?.mode ?? null);
  const [form, setForm] = useState(() => isEdit ? formFromItem(editItem) : {});
  const [errors, setErrors] = useState({});
  const [intercompanyConfig, setIntercompanyConfig] = useState(() => editItem?.intercompany_config ?? null);

  const { activeEntityId } = useEntityScope();

  // Catégorie choisie dans l'étape picker (quand categoryId non fourni)
  const [localCategoryId, setLocalCategoryId] = useState(null);
  const [localCategoryName, setLocalCategoryName] = useState('');
  const [localFlowType, setLocalFlowType] = useState(categoryFlowType);

  // Résout la catégorie effective (prop ou choisie localement)
  const effectiveCategoryId = categoryId ?? localCategoryId;
  const effectiveCategoryName = categoryName || localCategoryName;
  const effectiveFlowType = categoryFlowType || localFlowType;

  // Données pour le picker de catégories
  const { data: categoriesRaw } = useCategories();
  const allCategories = useMemo(() => {
    const list = Array.isArray(categoriesRaw)
      ? categoriesRaw
      : (categoriesRaw?.categories ?? categoriesRaw?.data ?? []);
    return list.filter((c) => c.status === 'active' || c.status == null);
  }, [categoriesRaw]);

  // Construit un arbre par flow_type : pour chaque flow (Dépenses / Revenus),
  // liste les racines triées alphabétiquement et leurs descendants (indentés).
  // Chaque "groupe" du dropdown = "{flow} — {root.name}" et contient la racine
  // + ses descendants ordonnés alphabétiquement (DFS).
  const categoryGroups = useMemo(() => {
    const collator = new Intl.Collator('fr', { sensitivity: 'base' });
    const childrenByParent = new Map();
    for (const c of allCategories) {
      const p = c.parent_id ?? null;
      if (!childrenByParent.has(p)) childrenByParent.set(p, []);
      childrenByParent.get(p).push(c);
    }
    for (const arr of childrenByParent.values()) {
      arr.sort((a, b) => collator.compare(a.name ?? '', b.name ?? ''));
    }

    function flatten(node, depth, acc) {
      acc.push({ ...node, depth });
      for (const child of childrenByParent.get(node.id) ?? []) {
        flatten(child, depth + 1, acc);
      }
      return acc;
    }

    function buildForFlow(flowType, flowLabel) {
      const roots = (childrenByParent.get(null) ?? []).filter((r) => r.flow_type === flowType);
      return roots.map((root) => ({
        flow_label: flowLabel,
        flow_type:  flowType,
        root,
        items:      flatten(root, 0, []),
      }));
    }

    return [
      ...buildForFlow('expense', 'Dépenses'),
      ...buildForFlow('income',  'Revenus'),
    ];
  }, [allCategories]);

  const createForecast = useCreateForecast();
  const updateForecast = useUpdateForecast();
  const createIndexed = useCreateIndexedExpense();
  const updateIndexed = useUpdateIndexedExpense();
  const createMonthlyBudget = useCreateMonthlyBudget();
  const updateMonthlyBudget = useUpdateMonthlyBudget();

  const { data: hypothesis } = useRevenueHypothesis();

  // Pour les modes standards, on utilise les mutations forecasts.
  // Pour indexed, on utilise les mutations indexed-expenses (option A — bypass /forecasts).
  // Pour monthly_budget, on utilise les mutations monthly-budgets (même pattern).
  const isIndexedMode = selectedMode === 'indexed';
  const isMonthlyBudgetMode = selectedMode === 'monthly_budget';
  const mutation = isMonthlyBudgetMode
    ? (isEdit ? updateMonthlyBudget : createMonthlyBudget)
    : isIndexedMode
    ? (isEdit ? updateIndexed : createIndexed)
    : (isEdit ? updateForecast : createForecast);

  // Reset à chaque ouverture
  useEffect(() => {
    if (isOpen) {
      if (isEdit) {
        setStep('form');
        setSelectedMode(editItem.mode);
        setForm(formFromItem(editItem));
        setIntercompanyConfig(editItem.intercompany_config ?? null);
      } else if (!categoryId) {
        setStep('category');
        setSelectedMode(null);
        setForm({});
        setIntercompanyConfig(null);
        setLocalCategoryId(null);
        setLocalCategoryName('');
        setLocalFlowType('expense');
      } else {
        setStep('mode');
        setSelectedMode(null);
        setForm({});
        setIntercompanyConfig(null);
      }
      setErrors({});
      mutation.reset?.();
    }
  }, [isOpen, editItem?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fermeture au clavier
  useEffect(() => {
    function handler(e) { if (e.key === 'Escape') onClose?.(); }
    if (isOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Verrou scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function handleSelectMode(mode) {
    setSelectedMode(mode);
    setForm(emptyForm(mode));
    setErrors({});
    setStep('form');
  }

  function handleBack() {
    if (step === 'form') {
      setStep(needsCategoryStep && !categoryId ? 'category' : 'mode');
    } else if (step === 'mode' && needsCategoryStep) {
      setStep('category');
    }
    setErrors({});
    mutation.reset?.();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate(selectedMode, form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const payload = buildPayload(selectedMode, form, effectiveCategoryId, effectiveFlowType);

    // Seuls recurring, one_shot et monthly_budget supportent intercompany_config
    const intercoModes = ['recurring', 'one_shot', 'monthly_budget'];
    const intercoPayload = intercoModes.includes(selectedMode)
      ? { intercompany_config: intercompanyConfig }
      : {};

    if (payload._isMonthlyBudget) {
      const { _isMonthlyBudget, ...mbPayload } = payload;
      if (isEdit) {
        await mutation.mutateAsync({ id: editItem.id, ...mbPayload, ...intercoPayload });
      } else {
        await mutation.mutateAsync({ ...mbPayload, ...intercoPayload });
      }
    } else if (payload._isIndexed) {
      // Option A : appel direct /indexed-expenses, pas /forecasts
      const { _isIndexed, ...indexedPayload } = payload;
      if (isEdit) {
        await mutation.mutateAsync({ id: editItem.id, ...indexedPayload });
      } else {
        await mutation.mutateAsync(indexedPayload);
      }
    } else if (isEdit) {
      await mutation.mutateAsync({ mode: selectedMode, id: editItem.id, ...payload, ...intercoPayload });
    } else {
      await mutation.mutateAsync({ ...payload, ...intercoPayload });
    }

    onClose?.();
  }

  if (!isOpen) return null;

  const modeConfig = MODES.find((m) => m.value === selectedMode);
  const isDisabled = mutation.isPending;

  const title = isEdit
    ? `Modifier — ${modeConfig?.label ?? ''}`
    : effectiveCategoryName
    ? `Ajouter dans ${effectiveCategoryName}`
    : 'Ajouter un prévisionnel';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        aria-hidden
        onClick={onClose}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl bg-white shadow-2xl max-h-[92dvh] overflow-y-auto sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-lg sm:rounded-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white flex items-center justify-between border-b border-slate-100 px-4 py-3 sm:px-6">
          {(step === 'form' && !isEdit) || (step === 'mode' && needsCategoryStep) ? (
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded"
              aria-label="Retour"
            >
              <ChevronLeft size={16} aria-hidden />
              Retour
            </button>
          ) : (
            <h2 className="text-base font-semibold text-slate-900">
              {step === 'category' ? 'Choisir une catégorie' : title}
            </h2>
          )}
          {step === 'form' && !isEdit && (
            <h2 className="text-base font-semibold text-slate-900 flex-1 text-center pr-8">
              {modeConfig?.label}
            </h2>
          )}
          {step === 'mode' && needsCategoryStep && (
            <h2 className="text-base font-semibold text-slate-900 flex-1 text-center pr-8">
              Type de prévisionnel
            </h2>
          )}
          <button
            onClick={onClose}
            className="ml-auto rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Étape 0 — Sélecteur de catégorie (quand ouvert sans categoryId) */}
        {step === 'category' && (
          <div className="px-4 py-5 sm:px-6 space-y-4">
            <p className="text-sm text-slate-500">
              Dans quelle catégorie souhaitez-vous créer ce prévisionnel ?
            </p>

            {categoryGroups.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">
                Aucune catégorie active disponible.
              </p>
            ) : (
              <Field label="Catégorie" required>
                <select
                  value={localCategoryId ?? ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id) return;
                    const cat = allCategories.find((c) => c.id === id);
                    if (!cat) return;
                    setLocalCategoryId(cat.id);
                    setLocalCategoryName(cat.name);
                    setLocalFlowType(cat.flow_type ?? 'expense');
                  }}
                  className={inputClass}
                >
                  <option value="">— Choisir une catégorie —</option>
                  {categoryGroups.map((group) => (
                    <optgroup
                      key={`${group.flow_type}-${group.root.id}`}
                      label={`${group.flow_label} — ${group.root.name}`}
                    >
                      {group.items.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.depth > 0
                            ? `${'  '.repeat(cat.depth)}↳ ${cat.name}`
                            : cat.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </Field>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => setStep('mode')}
                disabled={!localCategoryId}
                className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
              >
                Continuer
              </button>
            </div>
          </div>
        )}

        {/* Étape 1 — Sélecteur de mode */}
        {step === 'mode' && (
          <div className="px-4 py-5 sm:px-6 space-y-3">
            <p className="text-sm text-slate-500 mb-4">
              Choisissez le type de prévisionnel à créer pour{' '}
              <strong className="text-slate-700">{effectiveCategoryName}</strong>.
            </p>
            {MODES.filter((m) => !m.hidden).map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => handleSelectMode(m.value)}
                className={`w-full flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${m.color}`}
              >
                <m.Icon size={20} className="shrink-0 mt-0.5" aria-hidden />
                <div>
                  <p className="text-sm font-semibold">{m.label}</p>
                  <p className="text-xs opacity-80 mt-0.5">{m.description}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Étape 2 — Formulaire conditionnel */}
        {step === 'form' && selectedMode && (
          <form onSubmit={handleSubmit} className="px-4 py-5 sm:px-6 space-y-4">
            {selectedMode === 'recurring' && (
              <RecurringForm form={form} setField={setField} errors={errors} disabled={isDisabled} />
            )}
            {selectedMode === 'budget' && (
              <BudgetForm form={form} setField={setField} errors={errors} disabled={isDisabled} />
            )}
            {selectedMode === 'one_shot' && (
              <OneShotForm form={form} setField={setField} errors={errors} disabled={isDisabled} />
            )}
            {selectedMode === 'provision' && (
              <ProvisionForm form={form} setField={setField} errors={errors} disabled={isDisabled} />
            )}
            {selectedMode === 'indexed' && (
              <IndexedForm
                form={form}
                setField={setField}
                errors={errors}
                disabled={isDisabled}
                hypothesis={hypothesis}
              />
            )}
            {selectedMode === 'monthly_budget' && (
              <MonthlyBudgetForm form={form} setField={setField} errors={errors} disabled={isDisabled} />
            )}

            {/* Refacturation intercompany (recurring, one_shot, monthly_budget uniquement) */}
            {(['recurring', 'one_shot', 'monthly_budget'].includes(selectedMode)) && activeEntityId && (
              <IntercompanyConfigPanel
                value={intercompanyConfig}
                onChange={setIntercompanyConfig}
                fromEntityId={activeEntityId}
                amountCents={
                  selectedMode === 'monthly_budget'
                    ? eurosToCents(form.budget_amount)
                    : eurosToCents(form.amount)
                }
                sourceVatRate={
                  form.vatRate === 'custom'
                    ? parseFloat(form.customVatRate || '0')
                    : parseFloat(form.vatRate || '0')
                }
              />
            )}

            {mutation.error && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                {mutation.error.message}
              </p>
            )}

            <div className="flex gap-2 pt-1 pb-safe">
              <Button
                type="submit"
                loading={mutation.isPending}
                disabled={isDisabled}
                className="flex-1"
              >
                {isEdit ? 'Enregistrer' : 'Créer'}
              </Button>
              <Button type="button" variant="ghost" onClick={onClose} disabled={isDisabled}>
                Annuler
              </Button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
